import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import EditIcon from '@material-ui/icons/Edit';
import camelize from 'camelize';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { default as Select } from 'react-select';
import { Button, H1, H5, INLINE_COLORS, Label, ReceiptAccountInfo, ReceiptDisplay, Spinner, formatISODate, makeCreditPortalPath, reportError, useParentMessaging, windowOpenAndPrint, } from 'shared';
import { useLazyGetConsumerV2ReceiptsReceiptUuidQuery, usePatchConsumerV2ReceiptsReceiptUuidSignMutation, } from 'consumer-api';
import { useLazyGetConsumerV2ReceiptsReceiptUuidPDFQuery } from 'consumer-api-custom';
import { IdentityVerification } from '../components';
const ESign = () => {
    const history = useHistory();
    const { receiptIdEncoded } = useParams();
    const { sendReceiptSigned } = useParentMessaging();
    const [showSignField, setShowSignField] = useState(false);
    const [signed, setSigned] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [procedureDateInput, setProcedureDateInput] = useState('');
    const [ssnLast4, setSsnLast4] = useState('');
    const [signatureText, setSignatureText] = useState('');
    const [verificationComplete, setVerificationComplete] = useState(false);
    const [signConsumerReceipt] = usePatchConsumerV2ReceiptsReceiptUuidSignMutation();
    const [getReceipt, { data: receiptInfo, error, isFetching: isFetchingReceiptInfo },] = useLazyGetConsumerV2ReceiptsReceiptUuidQuery();
    const [getReceiptPDF, { isFetching: isFetchingReceiptPDF }] = useLazyGetConsumerV2ReceiptsReceiptUuidPDFQuery();
    const adjustedReceiptIdEncoded = receiptIdEncoded.includes('%3D')
        ? receiptIdEncoded.substring(0, receiptIdEncoded.indexOf('%3D'))
        : receiptIdEncoded;
    const receiptInfoDecoded = camelize(JSON.parse(atob(adjustedReceiptIdEncoded)));
    const isMobile = window.matchMedia('(pointer: coarse)').matches;
    const openPDF = () => {
        //@ts-expect-error
        getReceiptPDF({ receiptUuid: receiptInfoDecoded.receiptUuid })
            .then((response) => {
            if (response.data && response.data.pdfWindowUrl) {
                windowOpenAndPrint(response.data.pdfWindowUrl, true, !isMobile);
            }
        })
            .catch((e) => {
            const errMsg = 'PDF download and print failed';
            reportError('flashError', {
                componentName: 'ESign',
                funcName: 'getReceiptPDF',
                errType: 'pdf',
                errURL: window.location.pathname,
                errMsg: errMsg,
            }, e);
        });
    };
    useEffect(() => {
        //@ts-expect-error
        getReceipt({ receiptUuid: receiptInfoDecoded.receiptUuid });
    }, []);
    if (!receiptInfo && !error) {
        return _jsx(Spinner, {});
    }
    else if (error) {
        return _jsx("div", { className: "esign-container" });
    }
    else if (receiptInfo &&
        receiptInfo.signedAt &&
        !isMobile &&
        !isFetchingReceiptPDF) {
        openPDF();
        return _jsx(Spinner, {});
    }
    const receiptInfoFix = receiptInfo;
    const { transaction } = receiptInfoFix;
    const { account, consumerCreditInstrument, location, amount, authCode, referenceId, plan, accountMemberId, status, } = transaction;
    const accountOwner = account.accountMembers.find((member) => member.role === 'owner');
    if (status === 'voided' || status === 'canceled') {
        history.push({
            pathname: makeCreditPortalPath('/receipt/', receiptInfoDecoded.receiptUuid, `/transaction-${status}`),
            state: {
                from: '/receipt',
            },
        });
        return _jsx(_Fragment, {});
    }
    let authBuyer;
    if (account.accountMembers.length > 1) {
        const authBuyers = account.accountMembers.filter((member) => member.role === 'authorized_buyer');
        authBuyer = authBuyers.find((buyer) => buyer.accountMemberId === accountMemberId);
    }
    const paymentDetails = {
        alphaeonAccountNumber: consumerCreditInstrument.lenderAccountIdentifier,
        creditLimit: consumerCreditInstrument.creditLimit,
        name: {
            firstName: accountOwner.firstName,
            lastName: accountOwner.lastName,
        },
        availableCreditLimit: consumerCreditInstrument.creditAvailable,
        buyer: authBuyer
            ? { firstName: authBuyer.firstName, lastName: authBuyer.lastName }
            : null,
    };
    const placeholderText = 'Click here to sign';
    const selectOptions = [
        { value: 'today', label: 'Today/already happened' },
        { value: 'withinNextThirtyDays', label: 'Within the next 30 days' },
        {
            value: 'notScheduledOrOver30Days',
            label: 'Not scheduled or more than 30 days from now',
        },
    ];
    const handleSuccess = () => {
        history.push({
            pathname: makeCreditPortalPath('/receipt/', receiptInfoDecoded.receiptUuid, '/success'),
            state: {
                from: '/receipt',
            },
        });
    };
    const handleClick = () => {
        setShowSignField(true);
    };
    const handleSubmit = async () => {
        setSubmitting(true);
        const receiptUuid = receiptInfoDecoded.receiptUuid;
        const currentDate = new Date();
        const procedureDateMap = {
            today: formatISODate(currentDate),
            withinNextThirtyDays: formatISODate(new Date(currentDate.getTime() + 2592000000)), //adding 30 days in ms
            notScheduledOrOver30Days: undefined,
        };
        const signReceiptData = {
            receiptUuid: receiptUuid,
            patchConsumerV2ReceiptsReceiptUuidSign: {
                ssnLastFour: ssnLast4,
                procedureDate: procedureDateMap[procedureDateInput],
            },
        };
        //@ts-expect-error
        signConsumerReceipt(signReceiptData).then((res) => {
            if ('data' in res) {
                sendReceiptSigned({
                    status: 'signed',
                    transactionId: transaction.id,
                    receiptUuid: receiptInfoDecoded.receiptUuid,
                    amount,
                    referenceId,
                });
                handleSuccess();
            }
            else {
                sendReceiptSigned({
                    status: 'error',
                    transactionId: transaction.id,
                    receiptUuid: receiptInfoDecoded.receiptUuid,
                    amount,
                    referenceId,
                });
                reportError('flashError', {
                    componentName: 'ESign',
                    funcName: 'signConsumerReceipt',
                    errType: 'receipt',
                    errURL: window.location.pathname,
                    errMsg: 'Error signing receipt',
                });
                setSubmitting(false);
            }
        });
    };
    const handleSign = () => {
        setSigned(true);
        setSignatureText(`${accountOwner.firstName} ${accountOwner.lastName}`);
    };
    const handleProcedureDateInput = (newValue) => {
        if (newValue) {
            setProcedureDateInput(newValue.value);
        }
    };
    const handleLast4 = (e) => {
        if (/^[0-9]*$/.test(e.target.value) && e.target.value.length <= 4) {
            setSsnLast4(e.target.value);
        }
    };
    if (!isFetchingReceiptInfo &&
        !isFetchingReceiptPDF &&
        isMobile &&
        receiptInfoFix?.signedAt) {
        return (_jsx("div", { className: "esign-container", children: _jsx(Button, { type: "button", className: "button-warn", onClick: openPDF, submitting: isFetchingReceiptPDF, children: "Open Receipt" }) }));
    }
    if (isFetchingReceiptInfo ||
        isFetchingReceiptPDF ||
        receiptInfoFix?.signedAt) {
        return _jsx(Spinner, {});
    }
    if (receiptInfo?.identityVerificationRequired && !verificationComplete) {
        return (_jsx(IdentityVerification, { receiptUuid: receiptInfoDecoded.receiptUuid, onComplete: () => setVerificationComplete(true) }));
    }
    return (_jsxs("div", { className: "esign-container", children: [_jsx(H1, { children: " Patient Receipt" }), _jsx("div", { className: "account-summary summary refund-complete", children: _jsx(ReceiptAccountInfo, { buyer: authBuyer, paymentDetails: paymentDetails, currentLocation: location, name: account.accountMembers[0], alphaeonAccountNumber: consumerCreditInstrument.lenderAccountIdentifier, availableCreditLimit: consumerCreditInstrument.creditAvailable, creditLimit: consumerCreditInstrument.creditLimit, amount: amount, authorizationCode: authCode, referenceId: referenceId }) }), _jsx("div", { className: "receipt-display-container", children: _jsx(ReceiptDisplay, { name: {
                        firstName: accountOwner.firstName,
                        lastName: accountOwner.lastName,
                    }, currentLocation: location, selectedPlanName: plan.name, authorizationCode: authCode, alphaeonAccountNumber: consumerCreditInstrument.lenderAccountIdentifier, terms: plan.terms, amount: amount, isContractSignature: false }) }), !receiptInfoFix?.signedAt ? (_jsxs("div", { className: "esign-input-fields", children: [_jsxs("div", { className: "row", children: [_jsxs("div", { className: "ssn-input-container", children: [_jsx(Label, { htmlFor: "ssnInput", children: "SSN/ITIN # (Last 4 digits)" }), _jsx("input", { type: "tel", id: "ssnInput", name: "ssnInput", value: ssnLast4, onChange: handleLast4 })] }), _jsxs("div", { className: "procedure-date-input-container", children: [_jsx(Label, { htmlFor: "procedureDateInput", children: "Procedure Date" }), _jsx(Select, { styles: {
                                            control: (baseStyles) => ({
                                                ...baseStyles,
                                                border: 'none',
                                                borderRadius: '0',
                                                borderBottom: `1px solid ${INLINE_COLORS.blackBase}`,
                                                margin: 0,
                                                padding: 0,
                                            }),
                                            container: (baseStyles) => ({
                                                ...baseStyles,
                                                marginTop: '7px',
                                            }),
                                            singleValue: (baseStyles) => ({
                                                ...baseStyles,
                                                color: '#00000e',
                                            }),
                                            menu: (baseStyles) => ({
                                                ...baseStyles,
                                                borderRadius: 0,
                                            }),
                                            indicatorSeparator: (baseStyles) => ({
                                                ...baseStyles,
                                                display: 'none',
                                            }),
                                        }, options: selectOptions, onChange: handleProcedureDateInput, placeholder: "Select Date", value: selectOptions.filter(function (option) {
                                            return option.value === procedureDateInput;
                                        }) })] })] }), showSignField ? (_jsxs("div", { className: "sign-field", children: [_jsxs("div", { className: "signature", children: [_jsx(Label, { htmlFor: "signature-container", children: "Sign Here" }), _jsxs("div", { id: "signature-container", className: `text-container ${signatureText ? '' : 'pointer'}`, onClick: handleSign, role: "button", tabIndex: 0, onKeyDown: (e) => {
                                            if (e.key === 'Enter') {
                                                handleSign();
                                            }
                                        }, children: [_jsx("div", {}), signatureText ? (_jsx(H5, { variant: "body-large", className: "signature-text", children: signatureText })) : (_jsx(H5, { variant: "body-large", className: "placeholder-text", children: placeholderText })), _jsx(EditIcon, { fontSize: "small", className: "edit-signature-icon" })] })] }), _jsx(Button, { type: "button", className: "button-warn", onClick: handleSubmit, submitting: submitting, pristine: !signed || !procedureDateInput || ssnLast4.length < 4, children: "SUBMIT" })] })) : (_jsx("div", { className: "sign-and-submit-container", children: _jsx(Button, { type: "button", onClick: handleClick, className: "button-warn", pristine: !procedureDateInput || ssnLast4.length < 4, children: "CLICK TO SIGN & SUBMIT" }) }))] })) : (_jsx("div", { className: "esign-input-fields" }))] }));
};
export default ESign;
